import { Injectable } from "@angular/core";
import {
	DataProtectionConfigService,
	FraudPreventionConfigService,
	GroupIBFraudPreventionService,
} from "@obg/ngx.common";
import { CoreHttpClient, Market, ProductType } from "@obg/ngx.core";

import { RegistrationConfigService } from "../registration.config";
import { RegistrationFormFlow, RegistrationResponse } from "../registration.model";
import { RegistrationService } from "../registration.service";

import {
	SmartIdCompleteRegistrationPayload,
	SmartIdCompleteRegistrationRequest,
	SmartIdSimpleRegistrationPayload,
	SmartIdSimpleRegistrationRequest,
} from "./registration-with-smart-id.model";

@Injectable({
	providedIn: "root",
})
export class RegistrationWithSmartIdService {
	constructor(
		private httpClient: CoreHttpClient,
		private registrationConfig: RegistrationConfigService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private dataProtectionConfig: DataProtectionConfigService,
		private registrationService: RegistrationService
	) {}

	registerSmartIdSimple(payload: SmartIdSimpleRegistrationPayload, market: Market, product = ProductType.Common) {
		return this.httpClient.post<RegistrationResponse>({
			url: this.registrationConfig.getRegisterEndpoint(),
			data: this.buildSmartIdSimpleRegisterRequest(market, payload, product),
		});
	}

	registerSmartIdComplete(payload: SmartIdCompleteRegistrationPayload, market: Market, product = ProductType.Common) {
		return this.httpClient.post<RegistrationResponse>({
			url: this.registrationConfig.getRegisterEndpoint(),
			data: this.buildSmartIdCompleteRegisterRequest(payload, market, product),
		});
	}

	private buildSmartIdCompleteRegisterRequest(
		payload: SmartIdCompleteRegistrationPayload,
		market: Market,
		registeredFromProduct: ProductType
	): SmartIdCompleteRegistrationRequest {
		const simpleRequest = this.buildSmartIdSimpleRegisterRequest(market, payload, registeredFromProduct);
		const { person } = payload;
		return {
			...simpleRequest,
			person: {
				...simpleRequest.person,
				firstName: person.firstName,
				lastName: person.lastName,
				dateOfBirth: person.dateOfBirth,
			},
			address: {
				...simpleRequest.address,
			},
			flow: RegistrationFormFlow.Complete,
		};
	}

	private buildSmartIdSimpleRegisterRequest(
		market: Market,
		payload: SmartIdSimpleRegistrationPayload,
		registeredFromProduct: ProductType
	): SmartIdSimpleRegistrationRequest {
		const { additionalDetails, iovationBlackBox, legal, credential, gamingLimit, person, transactionId } = payload;

		const { street, zipCode, country, city, currency, phoneExtension, phoneNumber } = additionalDetails;

		const { gender, personalCode } = person;

		let consents = payload.consents;
		let marketingOffers = payload.marketingOffers;
		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && consents) {
			consents = {
				brandMarketing: consents.brandMarketing,
			};

			marketingOffers = {
				acceptEmailOffers: consents.brandMarketing,
				acceptSmsOffers: consents.brandMarketing,
				acceptTelesalesOffers: consents.brandMarketing,
			};
		}

		return {
			transactionId,
			language: market.languageCode,
			timeZone: "utc",
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			currency,
			person: {
				gender,
				personalCode,
			},
			address: {
				city,
				street,
				zipCode,
				country,
			},
			legal,
			credential: {
				...credential,
				username: credential.username || credential.email,
			},
			subscriptions: {
				...marketingOffers,
				email: credential.email,
				phoneExtension: phoneExtension.replace(/(^\+?)/, "+"),
				phoneNumber,
			},
			affiliateMetadata: this.registrationService.getAffiliateMetaData(),
			consents,
			gamingLimit,
			productMetadata: {
				registeredFromProduct,
			},
			flow: RegistrationFormFlow.Simple,
		};
	}
}
